import { ListStarIcon, ThumbsDownIcon, ThumbsUpIcon, XIcon } from '@phosphor-icons/react'
import * as Dialog from '@radix-ui/react-dialog'
import * as Popover from '@radix-ui/react-popover'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { match } from 'ts-pattern'
import z from 'zod'
import { useShowMutationError } from '../../hooks/use-show-mutation-error.tsx'
import { useToastService } from '../../services/toast-service.tsx'
import {
  invalidateGetFixFeedbackSubmissions,
  invalidateGetScaFeedbackSubmissions,
  invalidateGetTriageFeedbackSubmissions,
  useGetFixFeedbackSubmissions,
  useGetScaFeedbackSubmissions,
  useGetTriageFeedbackSubmissions,
  usePostFixFeedbackSubmission,
  usePostScaFeedbackSubmission,
  usePostTriageFeedbackSubmission,
} from '../../utils/api-client/api-hooks.ts'
import { type FeedbackSubmissionResponse } from '../../utils/api-client/api-schemas.ts'
import { formatDate } from '../../utils/date-utils.ts'
import * as styles from './feedback-bar.css.ts'
import { FeedbackForm, useFeedbackForm } from './feedback-form.tsx'
import { FloatingFooter } from './floating-footer.tsx'

// #region Types

export const FeedbackResultTypeSchema = z.enum(['sca', 'triage', 'fix'])
export type FeedbackResultType = z.infer<typeof FeedbackResultTypeSchema>

export type FeedbackBarProps = {
  resultId: string
  resultType: FeedbackResultType
  drawerContentRef: React.RefObject<HTMLDivElement | null>
}

// #endregion

// #region Hooks

const useFeedback = ({ resultId, resultType }: { resultId: string; resultType: FeedbackResultType }) => {
  const triageQuery = useGetTriageFeedbackSubmissions({ resultId, enabled: resultType === 'triage' })
  const fixQuery = useGetFixFeedbackSubmissions({ resultId, enabled: resultType === 'fix' })
  const scaQuery = useGetScaFeedbackSubmissions({ resultId, enabled: resultType === 'sca' })
  const triageMutation = usePostTriageFeedbackSubmission()
  const fixMutation = usePostFixFeedbackSubmission()
  const scaMutation = usePostScaFeedbackSubmission()
  const queryClient = useQueryClient()

  return match(resultType)
    .with('triage', () => ({
      query: triageQuery,
      mutation: triageMutation,
      invalidate: () => invalidateGetTriageFeedbackSubmissions(queryClient, { resultId }),
    }))
    .with('fix', () => ({
      query: fixQuery,
      mutation: fixMutation,
      invalidate: () => invalidateGetFixFeedbackSubmissions(queryClient, { resultId }),
    }))
    .with('sca', () => ({
      query: scaQuery,
      mutation: scaMutation,
      invalidate: () => invalidateGetScaFeedbackSubmissions(queryClient, { resultId }),
    }))
    .exhaustive()
}

// #endregion

// #region FeedbackBar

export const FeedbackBar: React.FC<FeedbackBarProps> = ({ resultId, resultType, drawerContentRef }) => {
  const form = useFeedbackForm()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)

  const { addToast } = useToastService()
  const showMutationError = useShowMutationError({ prefix: 'Memory could not be updated' })

  const {
    query: { data: existingFeedback },
    mutation: { mutate: postSubmission, isPending: isSubmitting },
    invalidate,
  } = useFeedback({ resultId, resultType })
  // Backend enforces at most one submission per result (POST returns 409 on
  // duplicate), so `items` is always 0 or 1 in length.
  const submission = existingFeedback?._embedded?.items?.[0]
  const isSubmitted = !!submission

  const handleBarIconClick = () => {
    if (isSubmitted) {
      setIsPopoverOpen(prev => !prev)
    } else {
      setIsModalOpen(true)
    }
  }

  const handleSubmit = () => {
    const { rating, text } = form.store.state.values
    if (!rating) {
      return
    }
    postSubmission(
      { resultId, rating, text },
      {
        onSuccess: () => {
          setIsModalOpen(false)
          form.reset()
          addToast({ message: <>Memory successfully updated</>, variant: 'success' })
        },
        onError: (error, ...args) => {
          if ('status' in error && error.status === 409) {
            invalidate()
            setIsModalOpen(false)
            form.reset()
            addToast({ message: <>Feedback was already submitted for this result</>, variant: 'info' })
          } else {
            showMutationError(error, ...args)
          }
        },
      }
    )
  }

  const handleModalClose = () => {
    setIsModalOpen(false)
    form.reset()
  }

  return (
    <form.AppForm>
      <FloatingFooter drawerContentRef={drawerContentRef} className={styles.feedbackFooter}>
        <div className={styles.barGroup}>
          <span
            className={isSubmitted ? styles.barLabelRated : styles.barLabel}
            role="button"
            tabIndex={0}
            onClick={handleBarIconClick}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                handleBarIconClick()
              }
            }}
          >
            Was this helpful?
          </span>

          {isSubmitted ? (
            <SubmittedBarIcon
              submission={submission}
              isPopoverOpen={isPopoverOpen}
              onIconClick={handleBarIconClick}
              onPopoverClose={() => setIsPopoverOpen(false)}
            />
          ) : (
            <button className={styles.barIconButton} onClick={handleBarIconClick} aria-label="Rate this result">
              <ListStarIcon size={16} />
            </button>
          )}
        </div>
      </FloatingFooter>

      <FeedbackModal
        isOpen={isModalOpen}
        isSubmitting={isSubmitting}
        onSubmit={handleSubmit}
        onClose={handleModalClose}
      />
    </form.AppForm>
  )
}

// #endregion

// #region SubmittedBarIcon

type SubmittedBarIconProps = {
  submission: FeedbackSubmissionResponse | undefined
  isPopoverOpen: boolean
  onIconClick: () => unknown
  onPopoverClose: () => unknown
}

const SubmittedBarIcon: React.FC<SubmittedBarIconProps> = ({
  submission,
  isPopoverOpen,
  onIconClick,
  onPopoverClose,
}) => (
  <Popover.Root
    open={isPopoverOpen}
    onOpenChange={open => {
      if (!open) onPopoverClose()
    }}
  >
    <Popover.Anchor asChild>
      <button
        className={styles.barIconButtonRated}
        onClick={onIconClick}
        aria-label={
          submission?.rating === 'positive' ? 'View submitted positive feedback' : 'View submitted negative feedback'
        }
      >
        {submission?.rating === 'positive' ? (
          <ThumbsUpIcon size={16} weight="fill" />
        ) : (
          <ThumbsDownIcon size={16} weight="fill" />
        )}
      </button>
    </Popover.Anchor>
    <Popover.Portal>
      <Popover.Content className={styles.popoverContent} side="top" align="end" sideOffset={8}>
        <div className={styles.popoverHeader}>
          <p className={styles.popoverMeta}>
            {submission?.createdAt && formatDate({ date: submission.createdAt, type: 'mm/dd/yyyy-hh:mm-ss' })}
            {submission?.username && <span> by @{submission.username}</span>}
          </p>
          <button className={styles.popoverCloseButton} onClick={onPopoverClose} aria-label="Close">
            <XIcon size={16} />
          </button>
        </div>
        {submission?.text && <p className={styles.popoverText}>{submission.text}</p>}
      </Popover.Content>
    </Popover.Portal>
  </Popover.Root>
)

// #endregion

// #region FeedbackModal

export type FeedbackModalProps = {
  isOpen: boolean
  isSubmitting: boolean
  onSubmit: () => unknown
  onClose: () => unknown
}

export const FeedbackModal: React.FC<FeedbackModalProps> = ({ isOpen, isSubmitting, onSubmit, onClose }) => (
  <Dialog.Root
    open={isOpen}
    onOpenChange={open => {
      if (!open) onClose()
    }}
  >
    <Dialog.Portal>
      <Dialog.Overlay className={styles.modalOverlay} />
      <Dialog.Content className={styles.modalContent} onEscapeKeyDown={onClose} aria-describedby={undefined}>
        <Dialog.Title className={styles.modalTitle}>Was this helpful?</Dialog.Title>
        <p className={styles.modalSubtitle}>What is your overall opinion of this result?</p>
        <FeedbackForm isSubmitting={isSubmitting} onSubmit={onSubmit} onCancel={onClose} />
      </Dialog.Content>
    </Dialog.Portal>
  </Dialog.Root>
)

// #endregion
